import { APP_SCREEN_ROUTES } from "../routes/screenRegistry";

/**
 * APP SCREEN LOCK REGISTRY
 * ------------------------
 * All `/app/...` routes from `screenRegistry.js` are **locked by default**:
 * they require a JWT, role shell + dynamic menu, and (unless unrestricted admin)
 * the current path must appear on that role's navigation.
 *
 * Add a `screenKey` here **only** when you intentionally want a screen to skip
 * login + menu (rare; local development only). Omitting new screens from a
 * "locked list" caused them to be treated as open → empty sidebar and broken UX.
 *
 * `notifications` stays locked (login required) but is allowed without a sidebar
 * menu entry — see PERSONAL_INBOX_SCREEN_KEYS in AppScreenAccessGuard.jsx.
 *
 * The app screen list (paths + components) was never here — it lives only in
 * `../routes/screenRegistry.js`. `REGISTERED_APP_SCREEN_KEYS` below is derived
 * from that file so you still have a single exported catalog of keys.
 */
/** Screens that skip login + menu checks until you move them out of this list. */

export const OPEN_APP_SCREEN_KEYS = ["my-memberships", "my-tournaments"];

const openSet = new Set(OPEN_APP_SCREEN_KEYS);

/**
 * The app screen list (paths + components) lives in `../routes/screenRegistry.js`.
 * `REGISTERED_APP_SCREEN_KEYS` below is derived from that file.
 */

/** True when this screen must use login + menu permission (not open URL). */
export function isAppScreenLocked(screenKey) {
  if (!screenKey) return true;
  return !openSet.has(screenKey);
}

/** Resolve /app/... pathname to a registry entry, if any. */
export function findAppScreenByPathname(pathname) {
  const raw = String(pathname ?? "").trim();
  if (!raw.startsWith("/app")) return null;

  let sub = raw.replace(/^\/app\/?/, "").replace(/\/$/, "");
  if (!sub) {
    return APP_SCREEN_ROUTES.find((r) => r.path === "user-builder") ?? null;
  }

  const exact = APP_SCREEN_ROUTES.find((r) => r.path === sub);
  if (exact) return exact;

  let best = null;
  for (const route of APP_SCREEN_ROUTES) {
    if (sub === route.path || sub.startsWith(`${route.path}/`)) {
      if (!best || route.path.length > best.path.length) {
        best = route;
      }
    }
  }
  return best;
}

/** True when direct URL access is allowed without login/nav check. */
export function isOpenAppPath(pathname) {
  const screen = findAppScreenByPathname(pathname);
  if (!screen) return false;
  return !isAppScreenLocked(screen.screenKey);
}

/** Unique screen keys registered under `/app` (for docs / tooling). */
export const REGISTERED_APP_SCREEN_KEYS = [
  ...new Set(APP_SCREEN_ROUTES.map((r) => r.screenKey).filter(Boolean)),
];
